import type z from 'zod';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import type { reportTypeSchema } from '@/features/patient-360/types/route-schema';
import type { Nullish } from '@/types/nullish';
import { useQueryExamRoomReport } from '@/features/patient-360/services/use-query-exam-room-report';
import { useQueryImageReport } from '@/features/patient-360/services/use-query-image-report';
import { useQueryPathologyReportContent } from '@/features/patient-360/services/use-query-pathology-report';
import { useQuerySurgeryReportContent } from '@/features/patient-360/services/use-query-surgery-report-content';

export type ReportType = z.infer<typeof reportTypeSchema>;

type UseReportContentParams = {
  type: ReportType | undefined;
  id: string | Nullish;
};

export const useReportContent = ({ type, id }: UseReportContentParams) => {
  const { t } = useTranslation(['patient-360']);
  const hasId = !!id;

  const surgeryQuery = useQuerySurgeryReportContent(id, {
    enabled: hasId && type === 'surgery',
  });

  const imageQuery = useQueryImageReport(id, {
    enabled: hasId && type === 'image',
  });

  const examQuery = useQueryExamRoomReport(id, {
    enabled: hasId && type === 'exam',
  });

  const pathologyQuery = useQueryPathologyReportContent(id, {
    enabled: hasId && type === 'pathology',
  });

  return match(type)
    .with('surgery', (reportType) => ({ reportType, query: surgeryQuery }))
    .with('image', (reportType) => ({ reportType, query: imageQuery }))
    .with('exam', (reportType) => ({ reportType, query: examQuery }))
    .with('pathology', (reportType) => ({ reportType, query: pathologyQuery }))
    .with(undefined, (reportType) => ({
      reportType,
      query: {
        data: null,
        isLoading: false,
        isError: true,
        error: new Error(t('patient-360:error.loadFailed')),
      },
    }))
    .exhaustive();
};
